// Default display names for the base plan codes stored on each organization; the names customers see (and any plans
// the admin adds) come from the plan catalog (usePlans / usePlanLabel).
export const PLAN_LABELS = {
  free: 'Free',
  starter: 'Business Basic',
  business: 'Business Standard',
  enterprise: 'Business Premium',
};

// Tiers: every plan follows one for its dashboard, features and limits (plans the admin adds included).
export const BASIC_PLAN = 'starter';
// Companies on this tier (named Business Premium in Subscription Control) use the Premium dashboard (premium/),
// served at /premium/ on their address.
export const PREMIUM_PLAN = 'business';
// Companies on this tier use the Enterprise dashboard (enterprices/), served at /enterprise/ on their address.
export const ENTERPRISE_PLAN = 'enterprise';

/**
 * A plan as the website and the app show it (from GET /api/public/plans):
 * { code, tier, name, imageUrl, priceType: 'fixed' | 'contact', price, yearlyPrice, cycle: 'monthly' | 'yearly' | 'both',
 *   annualDiscount, minUsers, maxUsers, badge, description, allowCoupons, buttonEnabled, buttonText, features, featureKeys,
 *   includes }
 */
const fromApi = (p, apiUrl = '') => ({
  code: p.plan,
  tier: p.tier,
  name: p.name,
  imageUrl: p.image_url ? apiUrl + p.image_url : null,
  priceType: p.price_type,
  price: p.price,
  yearlyPrice: p.yearly_price,
  cycle: p.billing_cycle,
  annualDiscount: p.annual_discount_pct,
  minUsers: p.min_users,
  maxUsers: p.max_users,
  badge: p.badge_text,
  description: p.short_description,
  allowCoupons: p.allow_coupons,
  buttonEnabled: p.button_enabled,
  buttonText: p.button_text,
  features: p.features,
  featureKeys: p.feature_keys || null, // Feature Access Control keys the plan includes (null: unknown)
});

// The built-in copy: used until the catalog loads, or if it can't. Matches BASE in backend/commerce/plans.py.
const BUILT_IN = [
  {
    plan: 'starter', price: 999, max_users: 10, badge_text: null,
    short_description: 'Perfect for small teams to get started with business calling.',
    features: ['Up to 10 users', '1 business phone number', 'Click-to-call from your browser', 'Visual IVR and call flow builder',
      'Office hours, holidays and team ringing', 'Voicemail to email', 'Call recording', 'Call history, search and export',
      'Contacts and appointments', 'Team roles and permissions'],
  },
  {
    plan: 'business', price: 1500, max_users: 50, badge_text: 'Most popular',
    short_description: 'For growing teams that need AI, messaging and automation.',
    features: ['Up to 50 users', 'More business phone numbers', 'Call queues and round-robin routing', 'AI receptionist',
      'WhatsApp and SMS', 'Advanced analytics and reports', 'Campaigns and automation', 'Priority support'],
  },
  {
    plan: 'enterprise', price: 1999, max_users: 500, badge_text: null,
    short_description: 'For large organisations with advanced security and integrations.',
    features: ['Users as you need', 'Toll-free and international numbers (add-on)', 'CRM integrations', 'Open API and webhooks',
      'Higher call capacity', 'IP allow-listing and advanced security', 'Uptime SLA', 'Dedicated account manager'],
  },
].map((p) => fromApi({
  ...p, tier: p.plan, name: PLAN_LABELS[p.plan], image_url: null, price_type: 'fixed', yearly_price: null,
  billing_cycle: 'monthly', annual_discount_pct: 0, min_users: 1, allow_coupons: true, button_enabled: true,
  button_text: `Choose ${PLAN_LABELS[p.plan]}`,
}));

// "Everything in <the plan before>, plus:" above each plan's features after the first, with the current names.
const withIncludes = (plans) => plans.map((p, i) => ({ ...p, includes: i ? `Everything in ${plans[i - 1].name}, plus:` : null }));

/** The plans from the catalog (`apiUrl` makes pictures' paths absolute), in the admin's order. */
export const buildPlans = (apiPlans, apiUrl) => withIncludes(apiPlans.map((p) => fromApi(p, apiUrl)));

export const DEFAULT_PLANS = withIncludes(BUILT_IN);

/** Back-compat name: the built-in plans. */
export const PAID_PLANS = DEFAULT_PLANS;

export const CALL_CHARGES_NOTE = 'Calls are charged per minute. Prices exclude GST.';

export const formatRupees = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
